"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/auth"
import {
  groupTenantBusinessesByOrganization,
  getDefaultActiveBusinessId,
} from "@/helpers/console-scope"
import type { AuthSession } from "@/types/auth"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AddCircle, AltArrowDown, CheckCircle, List, SortVertical, Upload } from "@solar-icons/react"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import Avatar from "@/components/core/avatar"
import { CreateBusinessTypeDialog } from "@/components/business/create-business-type-dialog"

interface Business {
  _id?: string
  name?: string
  image?: string
  description?: string
  location?: string
  parentBusiness?: string
}

interface BusinessSwitcherProps {
  businesses: Business[]
  activeBusiness?: Business
  onBusinessChange?: (business: Business) => void
  /** `header` matches admin header scope control placement (outline trigger). */
  variant?: "sidebar" | "header"
}

function businessLocationLabel(business: Business): string | undefined {
  const location = business.location?.trim()
  return location || undefined
}

export function BusinessSwitcher({
  businesses,
  activeBusiness,
  onBusinessChange,
  variant = "sidebar",
}: BusinessSwitcherProps) {
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const activeBusinessId = useAuthStore((state) => state.activeBusinessId)
  const setActiveBusiness = useAuthStore((state) => state.setActiveBusiness)
  const user = useAuthStore((state) => state.user)
  const [createTypeDialogOpen, setCreateTypeDialogOpen] = React.useState(false)

  const organizationGroups = React.useMemo(() => {
    const grouped = groupTenantBusinessesByOrganization(user as AuthSession | null)
    if (grouped.length > 0) {
      return grouped.map((group) => ({
        orgKey: group.orgKey,
        root: group.root as Business,
        branches: group.branches as Business[],
      }))
    }

    return businesses.map((business) => ({
      orgKey: business._id ?? "",
      root: business,
      branches: [] as Business[],
    }))
  }, [businesses, user])

  const flatBusinesses = React.useMemo(
    () =>
      organizationGroups.flatMap((group) => [group.root, ...group.branches]),
    [organizationGroups]
  )

  const currentActiveBusiness = React.useMemo(() => {
    if (activeBusinessId) {
      return (
        flatBusinesses.find((b) => b._id === activeBusinessId) ||
        activeBusiness ||
        flatBusinesses[0]
      )
    }
    return activeBusiness || flatBusinesses[0]
  }, [activeBusinessId, activeBusiness, flatBusinesses])

  React.useEffect(() => {
    if (activeBusinessId) return;
    const defaultBusinessId = getDefaultActiveBusinessId(user as AuthSession | null);
    if (defaultBusinessId) {
      setActiveBusiness(defaultBusinessId);
    }
  }, [activeBusinessId, user, setActiveBusiness])

  const handleBusinessSelect = (business: Business) => {
    if (business._id) {
      setActiveBusiness(business._id)
      onBusinessChange?.(business)
    }
  }

  const createLocationParentId = currentActiveBusiness
    ? currentActiveBusiness.parentBusiness || currentActiveBusiness._id
    : ""
  const createLocationParentName =
    organizationGroups.find((group) => group.orgKey === createLocationParentId)?.root
      .name ?? currentActiveBusiness?.name

  const handleAddBusiness = () => {
    if (!createLocationParentId) {
      navigate("/business/create")
      return
    }
    setCreateTypeDialogOpen(true)
  }

  const handleViewAll = () => {
    navigate("/business")
  }

  const handleImport = () => {
    navigate("/business/import")
  }

  if (!currentActiveBusiness && flatBusinesses.length === 0) {
    return null
  }

  const displayBusiness = currentActiveBusiness || flatBusinesses[0]

  const renderBusinessItem = (business: Business, shortcutIndex: number) => {
    const isActive = business._id === activeBusinessId
    const locationLabel = businessLocationLabel(business)

    return (
      <DropdownMenuItem
        key={business._id ?? `business-${shortcutIndex}`}
        onClick={() => handleBusinessSelect(business)}
        className="gap-2 p-2 border-b border-border rounded-none"
      >
        <Avatar
          src={business.image?.trim() ? business.image : undefined}
          alt={business.name ?? t("businessSwitcher.businessFallback")}
          size="sm"
          disabled
        />
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate">{business.name ?? t("businessSwitcher.businessFallback")}</span>
            {isActive && <CheckCircle className="w-4 h-4 text-primary shrink-0" />}
          </div>
          {locationLabel ? (
            <span className="truncate text-xs text-muted-foreground">{locationLabel}</span>
          ) : business.description ? (
            <span className="truncate text-xs text-muted-foreground">{business.description}</span>
          ) : null}
        </div>
        <DropdownMenuShortcut>⌘{shortcutIndex + 1}</DropdownMenuShortcut>
      </DropdownMenuItem>
    )
  }

  let shortcutIndex = 0

  const menuContent = (
    <>
      <DropdownMenuLabel className="text-muted-foreground text-xs">
        {t("businessSwitcher.label")}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {organizationGroups.map((group) => {
        const hasBranches = group.branches.length > 0
        const items: React.ReactNode[] = []

        if (hasBranches) {
          items.push(
            <DropdownMenuLabel
              key={`org-${group.orgKey}`}
              className="text-muted-foreground text-[11px] uppercase tracking-wide px-2 py-1"
            >
              {group.root.name ?? t("businessSwitcher.businessFallback")}
            </DropdownMenuLabel>
          )
        }

        for (const business of [group.root, ...group.branches]) {
          items.push(renderBusinessItem(business, shortcutIndex))
          shortcutIndex += 1
        }

        return <React.Fragment key={group.orgKey}>{items}</React.Fragment>
      })}
      <DropdownMenuItem onClick={handleViewAll} className="gap-2 p-2">
        <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
          <List className="size-4" />
        </div>
        <div className="text-muted-foreground font-medium">{t("businessSwitcher.viewAll")}</div>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleImport} className="gap-2 p-2">
        <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
          <Upload className="size-4" />
        </div>
        <div className="text-muted-foreground font-medium">{t("businessSwitcher.importViaExcel")}</div>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleAddBusiness} className="gap-2 p-2">
        <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
          <AddCircle className="size-4" />
        </div>
        <div className="text-muted-foreground font-medium">{t("businessSwitcher.addBusiness")}</div>
      </DropdownMenuItem>
    </>
  )

  const dropdownSide = variant === "header" ? "bottom" : isMobile ? "bottom" : "right"

  const typeDialog = (
    <CreateBusinessTypeDialog
      open={createTypeDialogOpen}
      onOpenChange={setCreateTypeDialogOpen}
      parentBusinessId={createLocationParentId || null}
      parentBusinessName={createLocationParentName}
    />
  )

  if (variant === "header") {
    return (
      <>
        {typeDialog}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-1.5 text-xs sm:max-w-[14rem] sm:px-2"
              data-tour-step-id="sidebar-business-switcher"
              aria-label={t("businessSwitcher.ariaLabelSwitch")}
            >
              <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full [&_img]:!h-6 [&_img]:!w-6 [&>div]:!h-6 [&>div]:!w-6 [&>div]:!min-h-0 [&>div]:!min-w-0 [&>div]:text-[10px]">
                <Avatar
                  src={displayBusiness?.image?.trim() ? displayBusiness.image : undefined}
                  alt={displayBusiness?.name ?? t("businessSwitcher.businessFallback")}
                  size="xs"
                  disabled
                />
              </span>
              <span className="hidden min-w-0 flex-1 truncate text-left font-medium sm:inline">
                {displayBusiness?.name ?? t("businessSwitcher.businessFallback")}
              </span>
              <AltArrowDown className="hidden h-3.5 w-3.5 shrink-0 opacity-60 sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56 w-[min(100vw-2rem,22rem)] rounded-lg"
            align="start"
            side={dropdownSide}
            sideOffset={4}
          >
            {menuContent}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    )
  }

  return (
    <>
      {typeDialog}
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                data-tour-step-id="sidebar-business-switcher"
              >
                <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                  <Avatar
                    src={displayBusiness?.image?.trim() ? displayBusiness.image : undefined}
                    alt={displayBusiness?.name ?? t("businessSwitcher.businessFallback")}
                    size="xs"
                    disabled
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <span className="truncate font-medium">{displayBusiness?.name ?? t("businessSwitcher.businessFallback")}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {businessLocationLabel(displayBusiness ?? {}) ||
                      displayBusiness?.description ||
                      t("businessSwitcher.noDescription")}
                  </span>
                </div>
                <SortVertical className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={dropdownSide}
              sideOffset={4}
            >
              {menuContent}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  )
}
